import React from 'react';
import { Shield, Target, Award, Users } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Om <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">Pignus Detektivbyrå</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Din pålitliga partner inom diskreta utredningar. Med över 5 års erfarenhet levererar vi resultat genom professionalism, moderna metoder och absolut konfidentialitet.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <Target className="w-12 h-12 text-teal-400 mb-6" />
              <h2 className="text-3xl font-bold text-white mb-4">Vårt Uppdrag</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Att ge dig klarhet i situationer där svar saknas.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                Genom diskret arbete, lagliga metoder och noggrann utredning tar vi fram information och bevis du kan agera på.
              </p>
              <p className="text-teal-400 font-semibold leading-relaxed">
                Varje uppdrag genomförs med ansvar, struktur och fullt fokus på resultat.
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <Shield className="w-12 h-12 text-teal-400 mb-6" />
              <h2 className="text-3xl font-bold text-white mb-4">Vår Vision</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Att vara det självklara valet för privatpersoner och företag som behöver tillförlitliga och diskreta utredningstjänster.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                Genom noggrant arbete, hög integritet och tydliga resultat bygger vi förtroende i varje uppdrag.
              </p>
              <p className="text-teal-400 font-semibold leading-relaxed">
                Din trygghet är grunden för vårt arbete.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">Vår Historia</h2>
          <div className="space-y-6 text-gray-400 leading-relaxed">
            <p>
              Pignus Detektivbyrå grundades av erfarna utredare med en gemensam vision: att erbjuda tillgängliga och professionella detektivtjänster till alla som behöver svar. Med över 5 års erfarenhet har vi etablerat oss som en pålitlig partner för både privatpersoner och företag över hela Sverige.
            </p>
            <p>
              Vår framgång bygger på tre grundpelare: <strong className="text-white">diskretion utan kompromisser</strong>, <strong className="text-white">moderna utredningsmetoder</strong> och <strong className="text-white">resultat som håller i rätten</strong>. Vi har hjälpt över 500 kunder att få klarhet i komplexa situationer – från misstankar om otrohet till företagsbedrägerier.
            </p>
            <p>
              Vi verkar i hela Sverige och kan snabbt mobilisera resurser oavsett var ärendet befinner sig. Vi kombinerar klassisk detektivkunskap med moderna digitala verktyg för att leverera bevis som håller juridisk standard. Alla våra utredningar genomförs enligt gällande lagar och med respekt för personlig integritet.
            </p>
            <p>
              Vi förstår att kontakta en detektivbyrå ofta är ett svårt steg. Därför erbjuder vi kostnadsfria konsultationer där vi lyssnar på din situation och föreslår en handlingsplan – helt utan förpliktelser. Din tillit är vårt viktigaste uppdrag.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">Våra Värderingar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Professionalism',
                desc: 'Högsta standard i allt vi gör'
              },
              {
                icon: Users,
                title: 'Diskretion',
                desc: 'Full sekretess och konfidentialitet'
              },
              {
                icon: Award,
                title: 'Erfarenhet',
                desc: '5 års branscherfarenhet'
              },
              {
                icon: Target,
                title: 'Resultat',
                desc: 'Levererar konkreta svar och bevis'
              }
            ].map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                <p className="text-gray-400 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Stats */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-teal-400 mb-2">5+</div>
              <div className="text-gray-400">År av erfarenhet</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-teal-400 mb-2">500+</div>
              <div className="text-gray-400">Nöjda kunder</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-teal-400 mb-2">24/7</div>
              <div className="text-gray-400">Tillgänglig support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">
            Varför välja oss?
          </h2>
          <div className="space-y-6">
            {[
              {
                title: 'Fullständig konfidentialitet',
                desc: 'När du anlitar oss är ditt ärende helt skyddat. Ingen utomstående får veta att du kontaktat oss och all kommunikation sker diskret. Din integritet är alltid vår högsta prioritet.'
              },
              {
                title: 'Moderna och effektiva metoder',
                desc: 'Vi kombinerar klassisk detektivkunskap med digital spaning, OSINT och avancerad analys. Det gör att vi kan arbeta snabbare, smartare och leverera konkreta resultat.'
              },
              {
                title: 'Bevis som håller juridiskt',
                desc: 'All dokumentation vi samlar in följer svensk lag och kan användas i juridiska sammanhang vid behov. Du får en professionell rapport med tydliga bevis och analys.'
              },
              {
                title: 'Snabb återkoppling och start',
                desc: 'Vi återkommer oftast inom 24 timmar och kan i många fall påbörja utredningar samma dag. Akuta ärenden prioriteras alltid.'
              },
              {
                title: 'Tydliga och transparenta priser',
                desc: 'Du får alltid en klar och tydlig offert innan vi påbörjar arbetet. Inga dolda avgifter eller oväntade kostnader.'
              },
              {
                title: 'Vi arbetar i hela Sverige',
                desc: 'Oavsett var ditt ärende finns kan vi snabbt mobilisera resurser och genomföra uppdrag över hela landet.'
              },
              {
                title: 'Diskret rådgivning utan förpliktelser',
                desc: 'Är du osäker på om du behöver en utredning? Kontakta oss för en kostnadsfri och konfidentiell första bedömning.'
              },
              {
                title: 'Erfarenhet och expertis',
                desc: 'Vi har erfarenhet av att hantera både enkla och komplexa ärenden för privatpersoner och företag. Du får en strukturerad och professionell utredning där inget lämnas åt slumpen.'
              },
              {
                title: 'Tydlig kommunikation genom hela processen',
                desc: 'Du hålls uppdaterad under hela utredningen och vet alltid vad som händer i ditt ärende. Vi är tillgängliga för frågor och ser till att du känner dig trygg genom hela processen.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;